import { getGuestInvitations } from '@/lib/credits-api'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  Clock3Icon,
  CoinsIcon,
  UsersIcon,
} from 'lucide-react'
import { format, addMonths, startOfDay } from 'date-fns'
import { de } from 'date-fns/locale'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { CourseArtwork } from '@/components/public/course-artwork'
import { PublicSessionCard } from '@/components/public/public-session-card'
import { SessionDetailDialog, type SessionDetailData } from '@/components/session-detail-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  bookSessionWithCredit,
  cancelMemberBooking,
  getCourses,
  getCurrentUserRole,
  getMemberBookings,
  getPublicSessionById,
  getPublicSessions,
  getSessionBookingQuote,
  getUserSubscriptionStatus,
} from '@/lib/api'
import { getCreditWeekStartKey } from '@/lib/date'
import { REGULAR_CREDIT_TYPE } from '@/lib/credits-api'
import { useTypedCreditBalances } from '@/hooks/use-typed-credit-balances'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { scrollAppLayoutToTop } from '@/lib/scroll-container'
import {
  formatCourseCredits,
  formatCredits,
  isRecurringSubscription,
  isSubscriptionActiveAt,
  memberBookingBlockMessage,
} from '@/lib/subscriptions'
import { cn } from '@/lib/utils'
import type { Booking, Course, PublicSession, SessionBookingQuote } from '@/types/domain'

type CourseDetailLocationState = {
  dialogSession?: PublicSession
  dialogBooking?: Booking
  fromCoursesOverview?: boolean
}

function publicSessionToDetail(
  session: PublicSession,
  quote?: SessionBookingQuote,
): SessionDetailData {
  return {
    sessionId: session.session_id,
    courseName: session.course_name,
    courseDescription: session.description,
    startsAt: session.starts_at,
    endsAt: session.ends_at,
    trainerName: session.trainer_name,
    trainerId: session.trainer_id,
    level: session.level,
    availableSpots: session.available_spots,
    maxParticipants: session.max_participants,
    confirmedCount: session.confirmed_count,
    resourceName: session.resource_name,
    registrationUntil: session.registration_until,
    cancellationUntil: session.cancellation_until,
    minParticipants: session.min_participants,
    creditsRequired: quote?.credits_required ?? session.credits_per_session,
    subscriptionCreditsUsed: quote?.subscription_credits_used ?? null,
    balanceCreditsUsed: quote?.balance_credits_used ?? null,
  }
}

function bookingToSessionDetail(booking: Booking): SessionDetailData {
  const session = booking.course_sessions
  const availableSpots =
    session ? Math.max(session.max_participants - session.confirmed_count, 0) : null

  return {
    bookingId: booking.id,
    sessionId: booking.session_id,
    courseName: session?.courses?.name ?? 'Kurs',
    courseDescription: session?.courses?.description ?? null,
    startsAt: session?.starts_at ?? '',
    endsAt: session?.ends_at ?? '',
    trainerName: session?.trainer_name ?? null,
    trainerId: session?.trainer_id ?? null,
    level: session?.courses?.level ?? null,
    availableSpots,
    maxParticipants: session?.max_participants ?? null,
    status: booking.status === 'confirmed' ? 'confirmed' : 'cancelled',
    registrationUntil: session?.registration_until ?? null,
    cancellationUntil: session?.cancellation_until ?? null,
    minParticipants: session?.min_participants ?? null,
    confirmedCount: session?.confirmed_count ?? null,
    resourceName: session?.resource_name ?? null,
    creditsRequired: booking.credits_required ?? session?.courses?.credits_per_session ?? null,
    subscriptionCreditsUsed: booking.subscription_credits_used ?? null,
    balanceCreditsUsed: booking.balance_credits_used ?? null,
  }
}

export function CourseDetailPage({ courseIdOverride }: { courseIdOverride?: string } = {}) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const hasScrolledToTopRef = useRef(false)
  const { courseId: routeCourseId, sessionId } = useParams<{ courseId?: string; sessionId?: string }>()
  const courseId = courseIdOverride ?? routeCourseId
  const routeState = location.state as CourseDetailLocationState | null
  const cameFromCoursesOverview = routeState?.fromCoursesOverview === true
  const seededSession =
    sessionId && routeState?.dialogSession?.session_id === sessionId ? routeState.dialogSession : null
  const seededBooking =
    sessionId && routeState?.dialogBooking?.session_id === sessionId ? routeState.dialogBooking : null
  const [bookingSession, setBookingSession] = useState<PublicSession | null>(seededSession)
  const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(seededBooking)
  const today = useMemo(() => startOfDay(new Date()), [])
  const nextYear = useMemo(() => addMonths(today, 12), [today])

  const coursesQuery = useQuery({
    queryKey: queryKeys.publicCourses,
    queryFn: () => getCourses(false),
  })

  const sessionsQuery = useQuery({
    queryKey: queryKeys.publicSessions(today.toISOString(), nextYear.toISOString()),
    queryFn: () => getPublicSessions(today.toISOString(), nextYear.toISOString()),
  })

  const directSessionQuery = useQuery({
    queryKey: sessionId ? queryKeys.publicSession(sessionId) : ['public-session', 'none'],
    queryFn: () => getPublicSessionById(sessionId as string),
    enabled: Boolean(sessionId),
    placeholderData: seededSession ?? undefined,
    retry: false,
  })

  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.userSubscription,
    queryFn: getUserSubscriptionStatus,
    enabled: profileQuery.data?.role === 'member',
  })

  const memberBookingsQuery = useQuery({
    queryKey: queryKeys.memberBookingsUpcoming,
    queryFn: () => getMemberBookings({ upcomingOnly: true }),
    enabled: profileQuery.data?.role === 'member',
    retry: false,
    refetchOnWindowFocus: false,
  })

  const guestInvitesQuery = useQuery({ queryKey: ['guest-invitations'], queryFn: () => getGuestInvitations(), enabled: profileQuery.data?.role === 'member' })
  const guestBooking = guestInvitesQuery.data?.find(invite => invite.session_id === bookingSession?.session_id)

  const bookingQuoteQuery = useQuery({
    queryKey: queryKeys.sessionBookingQuote(bookingSession?.session_id ?? 'none'),
    queryFn: () => getSessionBookingQuote(bookingSession!.session_id),
    enabled: profileQuery.data?.role === 'member' && bookingSession !== null,
    retry: false,
  })

  const resolvedCourseId = courseId ?? directSessionQuery.data?.course_id ?? null

  const course = useMemo<Course | null>(() => {
    if (!resolvedCourseId) {
      return null
    }

    return (coursesQuery.data ?? []).find((entry) => entry.id === resolvedCourseId) ?? null
  }, [coursesQuery.data, resolvedCourseId])

  const courseSessions = useMemo(() => {
    if (!resolvedCourseId) {
      return []
    }

    return (sessionsQuery.data ?? [])
      .filter((session) => session.course_id === resolvedCourseId)
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
  }, [resolvedCourseId, sessionsQuery.data])

  const courseWeekStarts = useMemo(
    () => Array.from(new Set(courseSessions.map((session) => getCreditWeekStartKey(session.starts_at)))).sort(),
    [courseSessions],
  )

  const creditBalancesQuery = useTypedCreditBalances({
    weekStarts: courseWeekStarts,
    enabled: profileQuery.data?.role === 'member',
  })

  const courseCreditTypeId = course?.credit_type_id ?? REGULAR_CREDIT_TYPE
  const creditBalanceByWeekStart = useMemo(
    () =>
      new Map(
        (creditBalancesQuery.data ?? [])
          .filter((balance) => balance.credit_type_id === courseCreditTypeId)
          .map((balance) => [balance.week_start, balance]),
      ),
    [creditBalancesQuery.data, courseCreditTypeId],
  )

  const bookingBySessionId = useMemo(() => {
    const bySessionId = new Map<string, Booking>()

    for (const booking of memberBookingsQuery.data ?? []) {
      if (booking.status !== 'confirmed') {
        continue
      }
      bySessionId.set(booking.session_id, booking)
    }

    return bySessionId
  }, [memberBookingsQuery.data])

  const isCheckingExistingBookings = profileQuery.data?.role === 'member' && memberBookingsQuery.isLoading
  const bookingBlockMessage = memberBookingBlockMessage(bookingQuoteQuery.data)
  const insufficientCredits = bookingQuoteQuery.data?.can_book === false && !bookingBlockMessage
  const isBookingDeadlinePassed = bookingSession?.registration_until
    ? new Date(bookingSession.registration_until) <= new Date()
    : false
  const bookingSessionHasActiveSubscription = bookingSession
    ? isSubscriptionActiveAt(subscriptionQuery.data, new Date(bookingSession.starts_at))
    : false
  const selectedSessionCreditBalance = bookingSession
    ? creditBalanceByWeekStart.get(getCreditWeekStartKey(bookingSession.starts_at))
    : undefined

  const bookMutation = useMutation({
    mutationFn: bookSessionWithCredit,
    onSuccess: () => {
      toast.success('Kurs erfolgreich gebucht!')
      setBookingSession(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.publicSessionsBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookingsUpcoming })
      if (resolvedCourseId) {
        navigate(`/kurse/${resolvedCourseId}`, { replace: true, preventScrollReset: true })
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const cancelMutation = useMutation({
    mutationFn: cancelMemberBooking,
    onSuccess: () => {
      toast.success('Buchung erfolgreich storniert.')
      setCancellingBooking(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.publicSessionsBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookingsUpcoming })
      if (resolvedCourseId) {
        navigate(`/kurse/${resolvedCourseId}`, { replace: true, preventScrollReset: true })
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  useEffect(() => {
    if (!sessionId) {
      return
    }

    // Opens the dialog of a deep link once the route state is there; an effect on purpose (see the effect below).
    if (seededBooking) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBookingSession(null)
      setCancellingBooking(seededBooking)
      return
    }

    if (seededSession) {
      setCancellingBooking(null)
      setBookingSession(seededSession)
    }
  }, [seededBooking, seededSession, sessionId])

  useLayoutEffect(() => {
    if (hasScrolledToTopRef.current || sessionId || !cameFromCoursesOverview) {
      return
    }

    hasScrolledToTopRef.current = true
    scrollAppLayoutToTop()
  }, [cameFromCoursesOverview, sessionId])

  const handleBackToCourses = () => {
    navigate('/kurse', {
      preventScrollReset: true,
      state: { restoreCoursesOverview: true },
    })
  }

  useEffect(() => {
    if (!sessionId || !directSessionQuery.data) {
      return
    }

    if (profileQuery.data?.role === 'member' && memberBookingsQuery.isLoading) {
      return
    }

    const session = directSessionQuery.data
    const existingBooking = bookingBySessionId.get(session.session_id)

    // Syncs the dialog with the deep link after the session and the member's bookings have loaded, together with the
    // redirect to the canonical URL; deriving it during render would also need the navigation side effect.
    if (existingBooking) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBookingSession(null)
      setCancellingBooking(existingBooking)
    } else {
      setCancellingBooking(null)
      setBookingSession(session)
    }

    if (courseId !== session.course_id) {
      navigate(`/kurse/${session.course_id}/termine/${session.session_id}`, {
        replace: true,
        preventScrollReset: true,
      })
    }
  }, [
    bookingBySessionId,
    courseId,
    directSessionQuery.data,
    memberBookingsQuery.isLoading,
    navigate,
    profileQuery.data?.role,
    sessionId,
  ])

  useEffect(() => {
    if (!sessionId || !directSessionQuery.isError) {
      return
    }

    toast.error('Der angeforderte Termin konnte nicht geladen werden.')
    navigate('/kurse', { replace: true })
  }, [directSessionQuery.isError, navigate, sessionId])

  const nextSession = courseSessions[0] ?? null
  const hasOpenDialogContext = bookingSession !== null || cancellingBooking !== null
  const isLoading =
    coursesQuery.isLoading ||
    sessionsQuery.isLoading ||
    (Boolean(sessionId) && directSessionQuery.isLoading && !hasOpenDialogContext)
  const hasError = coursesQuery.isError || sessionsQuery.isError

  if (isLoading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-10 w-36" />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <Skeleton className="aspect-[4/3] w-full rounded-2xl" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (hasError || !course) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          className="w-fit px-0 text-muted-foreground"
          onClick={handleBackToCourses}
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Zurück zur Kursübersicht
        </Button>
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Dieser Kurs konnte nicht geladen werden.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <Button
        variant="ghost"
        size="sm"
        className="w-fit px-0 text-muted-foreground"
        onClick={handleBackToCourses}
      >
        <ArrowLeftIcon className="mr-2 h-4 w-4" />
        Zurück zur Kursübersicht
      </Button>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start">
        <CourseArtwork
          course={course}
          className="aspect-[4/3] rounded-2xl border border-border/70 shadow-sm"
          imageClassName="rounded-2xl"
          labelClassName="text-base"
        />

        <div className="space-y-5">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-semibold tracking-tight">{course.name}</h1>
              <Badge variant="secondary">{course.duration_minutes} Minuten</Badge>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              {course.description?.trim() || 'Für diesen Kurs ist aktuell keine Beschreibung hinterlegt.'}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="border-border/70">
              <CardContent className="flex items-center gap-3 p-4">
                <CoinsIcon className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Kurswert</p>
                  <p className="text-sm font-medium text-foreground">
                    {formatCourseCredits(course.credits_per_session)}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardContent className="flex items-center gap-3 p-4">
                <CalendarDaysIcon className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Nächster Termin</p>
                  <p className="text-sm font-medium text-foreground">
                    {nextSession
                      ? format(new Date(nextSession.starts_at), 'dd.MM.yyyy', { locale: de })
                      : 'Noch offen'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardContent className="flex items-center gap-3 p-4">
                <Clock3Icon className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Dauer</p>
                  <p className="text-sm font-medium text-foreground">{course.duration_minutes} Minuten</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/70">
              <CardContent className="flex items-center gap-3 p-4">
                <UsersIcon className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Teilnehmer</p>
                  <p className="text-sm font-medium text-foreground">
                    Max. {course.default_max_participants}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {courseSessions.some(session => (session.min_participants ?? 0) > 1) && (
        <Alert className="border-blue-200 bg-blue-50 text-blue-900">
          <AlertDescription className="text-xs md:text-sm">
            <strong>Hinweis:</strong> Unsere Kurse finden erst ab Erreichen der angegebenen
            Mindest-Teilnehmerzahl statt. Sollte ein Kurs nicht zustande kommen, wirst du
            rechtzeitig per E-Mail informiert.
          </AlertDescription>
        </Alert>
      )}

      {profileQuery.data?.role === 'member' &&
        (!course.credit_type_id || course.credit_type_id === '00000000-0000-0000-0000-000000000001') &&
        subscriptionQuery.data &&
        course.credits_per_session > 0 &&
        subscriptionQuery.data.remaining_credits <= 0 &&
        !isRecurringSubscription(subscriptionQuery.data) && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs font-medium md:text-sm">
              Dein Guthaben ist aufgebraucht. Bitte lade Kursguthaben nach, um weitere kostenpflichtige Kurse buchen
              zu können.
            </AlertDescription>
          </Alert>
        )}

      <section className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Nächste Termine</h2>
        </div>

        {memberBookingsQuery.isError && profileQuery.data?.role === 'member' && (
          <Card>
            <CardContent className="p-6 text-sm text-destructive">
              {getErrorMessage(memberBookingsQuery.error)}
            </CardContent>
          </Card>
        )}

        {courseSessions.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              Für diesen Kurs sind aktuell noch keine kommenden Termine geplant.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {courseSessions.map((session) => (
              <PublicSessionCard
                key={session.session_id}
                session={session}
                isCheckingExistingBookings={isCheckingExistingBookings}
                hasExistingBooking={bookingBySessionId.has(session.session_id)}
                showFullTrainerName={profileQuery.data?.role === 'admin'}
                onBook={(selectedSession) => {
                  setCancellingBooking(null)
                  setBookingSession(selectedSession)
                  navigate(`/kurse/${course.id}/termine/${selectedSession.session_id}`, {
                    preventScrollReset: true,
                    state: { dialogSession: selectedSession } satisfies CourseDetailLocationState,
                  })
                }}
                onCancel={(selectedSession) => {
                  const booking = bookingBySessionId.get(selectedSession.session_id)
                  if (!booking) {
                    return
                  }

                  setBookingSession(null)
                  setCancellingBooking(booking)
                  navigate(`/kurse/${course.id}/termine/${selectedSession.session_id}`, {
                    preventScrollReset: true,
                    state: {
                      dialogBooking: booking,
                      dialogSession: selectedSession,
                    } satisfies CourseDetailLocationState,
                  })
                }}
              />
            ))}
          </div>
        )}
      </section>

      <SessionDetailDialog
        open={bookingSession !== null || cancellingBooking !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBookingSession(null)
            setCancellingBooking(null)
            navigate(`/kurse/${course.id}`, { replace: true, preventScrollReset: true })
          }
        }}
        session={
          cancellingBooking
            ? bookingToSessionDetail(cancellingBooking)
            : bookingSession
              ? { ...publicSessionToDetail(bookingSession, guestBooking ? undefined : bookingQuoteQuery.data), ...(guestBooking ? { bookingId: guestBooking.booking_id, creditsRequired: 0, subscriptionCreditsUsed: 0, balanceCreditsUsed: 0 } : {}) }
              : null
        }
        title={guestBooking ? "Gastteilnahme" : cancellingBooking ? "Buchungsdetails" : "Kurs buchen"}
        description={
          guestBooking ? 'Informationen zu deiner Einladung.' : cancellingBooking
            ? 'Hier kannst du deine Buchung und Gäste verwalten.'
            : 'Möchtest du dich für folgenden Kurs anmelden?'
        }
        onCancel={
          cancellingBooking ? () => cancelMutation.mutate(cancellingBooking.id) : undefined
        }
        cancelPending={cancelMutation.isPending}
        footer={
          guestBooking ? <div className="w-full space-y-2 text-sm"><p>{guestBooking.status === "accepted" ? "Du nimmst als Gast teil. Für dich werden keine Credits abgezogen." : "Für diesen Termin liegt eine Einladung für dich vor."}</p><Button asChild><a href="/meine-kurse">Einladung und Teilnahme verwalten</a></Button></div> : bookingSession ? (
            <>
              {isBookingDeadlinePassed || bookingBlockMessage || insufficientCredits ? (
                <div className="flex w-full flex-col gap-3">
                  {isBookingDeadlinePassed || bookingBlockMessage ? (
                    <p className="text-sm text-muted-foreground">
                      {bookingBlockMessage ?? 'Buchungsfrist abgelaufen'}
                    </p>
                  ) : (
                    <Alert variant="destructive" className="bg-destructive/5">
                      <AlertCircleIcon />
                      <AlertTitle>Nicht genügend Credits</AlertTitle>
                      <AlertDescription>
                        <p>
                          Benötigt werden{' '}
                          <strong>
                            {formatCourseCredits(
                              bookingQuoteQuery.data?.credits_required ??
                                bookingSession.credits_per_session,
                            )}
                          </strong>
                          {'.'}
                        </p>
                        <div
                          className={cn(
                            'mt-3 grid gap-2',
                            bookingSessionHasActiveSubscription ? 'grid-cols-2' : 'grid-cols-1',
                          )}
                        >
                          {bookingSessionHasActiveSubscription && (
                            <div className="rounded-md border border-destructive/20 bg-background/80 px-2.5 py-2 text-foreground">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                Abo verfügbar
                              </p>
                              <p className="mt-0.5 text-sm font-semibold">
                                {selectedSessionCreditBalance?.has_unlimited_subscription
                                  ? 'Unbegrenzt'
                                  : formatCredits(
                                      selectedSessionCreditBalance
                                        ?.available_subscription_credits ?? 0,
                                    )}
                              </p>
                            </div>
                          )}
                          <div className="rounded-md border border-destructive/20 bg-background/80 px-2.5 py-2 text-foreground">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              {bookingQuoteQuery.data?.credit_name
                                ? `${bookingQuoteQuery.data.credit_name} verfügbar`
                                : 'Guthaben verfügbar'}
                            </p>
                            <p className="mt-0.5 text-sm font-semibold">
                              {formatCredits(
                                bookingQuoteQuery.data?.available_balance_credits ?? 0,
                              )}
                            </p>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="flex w-full justify-end gap-2">
                    {insufficientCredits && new Date(bookingSession.starts_at) > new Date() && bookingSession.available_spots > 0 && !isBookingDeadlinePassed && !bookingBlockMessage && (
                      <Button asChild>
                        <Link to={`/abo?angebote=1&zurueck=${encodeURIComponent(`/kurse/${bookingSession.course_id}/termine/${bookingSession.session_id}`)}`}>
                          Credits kaufen
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setBookingSession(null)
                        navigate(`/kurse/${course.id}`, {
                          replace: true,
                          preventScrollReset: true,
                        })
                      }}
                    >
                      Schließen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex w-full flex-col gap-3">
                  <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 text-primary">
                        <CoinsIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-foreground">Credit-Abrechnung</p>
                          <span className="shrink-0 rounded-full border border-primary/20 bg-background px-2 py-0.5 text-xs font-semibold text-foreground">
                            {formatCourseCredits(
                              bookingQuoteQuery.data?.credits_required ??
                                bookingSession.credits_per_session,
                            )}
                          </span>
                        </div>
                        {(bookingQuoteQuery.data?.credits_required ?? bookingSession.credits_per_session) === 0 ? (
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            Kostenlos – es werden keine Credits abgezogen.
                          </p>
                        ) : bookingQuoteQuery.isLoading || subscriptionQuery.isLoading ? (
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            Die Aufteilung wird berechnet ...
                          </p>
                        ) : bookingQuoteQuery.data ? (
                          <div className="space-y-2">
                            <p className="mt-2 text-xs text-muted-foreground">
                              Verfügbares Guthaben ({bookingQuoteQuery.data.credit_name ?? 'Credits'}):{' '}
                              <strong>{formatCredits(bookingQuoteQuery.data.available_balance_credits)}</strong>
                            </p>
                            <p className="text-xs text-muted-foreground">Für diese Buchung werden verwendet:</p>
                            <div
                              className={cn(
                                'mt-2 grid gap-2',
                                bookingQuoteQuery.data.balance_credits_used > 0 &&
                                  bookingSessionHasActiveSubscription
                                  ? 'grid-cols-2'
                                  : 'grid-cols-1',
                              )}
                            >
                              {bookingSessionHasActiveSubscription && (
                                <div className="rounded-md border bg-background/80 px-2.5 py-2">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    Aus Abo
                                  </p>
                                  <p className="mt-0.5 text-sm font-semibold text-foreground">
                                    {formatCredits(
                                      bookingQuoteQuery.data.subscription_credits_used,
                                    )}
                                  </p>
                                </div>
                              )}
                              {bookingQuoteQuery.data.balance_credits_used > 0 && (
                                <div className="rounded-md border bg-background/80 px-2.5 py-2">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    Aus Guthaben
                                  </p>
                                  <p className="mt-0.5 text-sm font-semibold text-foreground">
                                    {formatCredits(bookingQuoteQuery.data.balance_credits_used)}
                                  </p>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Guthaben nach dieser Buchung:{' '}
                              <strong>{formatCredits(Math.max(0, bookingQuoteQuery.data.available_balance_credits - bookingQuoteQuery.data.balance_credits_used))}</strong>
                            </p>
                          </div>
                        ) : (
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            Die genaue Aufteilung wird beim Buchen geprüft.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setBookingSession(null)
                        navigate(`/kurse/${course.id}`, {
                          replace: true,
                          preventScrollReset: true,
                        })
                      }}
                      disabled={
                        bookMutation.isPending ||
                        bookingQuoteQuery.isLoading ||
                        subscriptionQuery.isLoading
                      }
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={() => bookingSession && bookMutation.mutate(bookingSession.session_id)}
                      disabled={
                        bookMutation.isPending ||
                        bookingQuoteQuery.isLoading ||
                        subscriptionQuery.isLoading
                      }
                    >
                      {bookMutation.isPending ? 'Wird gebucht...' : 'Jetzt buchen'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : undefined
        }
      />
    </div>
  )
}
